<template>
  <!-- NOTE: q-page sets min-height, but not height, which results in descendents
      not having a set height or min-height, unless explicitly set.
      using % on descendents height will fail unless we wrap everything in a grid layout: -->
  <q-page
    class="page"
    :class="[
      isStacked ? 're-stacked' : 're-side-by-side',
      $slots.sidebar || flow ? 'with-sidebar' : null,
      isCentered && !q.screen.xs ? 're-centered' : null,
    ]"
  >
    <!-- optional sidebar it if exists -->
    <div
      v-if="$slots.sidebar || (flow && !flow.currentStepMeta.isStepperHidden)"
      class="sidebar"
      :class="{ 'sticky-top': stickyTop, 'limit-width-above-sm': !isStacked }"
    >
      <slot
        v-if="$slots.sidebar"
        name="sidebar"
      />
      <re-stepper
        v-else-if="flow && flow.isEnabled"
        :flow="flow"
        v-bind="flowProps"
        :is-horizontal="isStacked"
        :mark-steps-done="markStepsDone"
      >
        <template
          v-for="slot in Object.keys($slots).filter((s) => s.startsWith('stepper-'))"
          #[slot]
        >
          <slot :name="slot" />
        </template>
      </re-stepper>
    </div>
    <div
      class="page__main column"
      :class="{
        [`width-${maxWidth}`]: maxWidth !== 'full',
      }"
    >
      <template v-if="$slots.banner">
        <slot name="banner" />
      </template>
      <div
        :class="{
          [mainContentClass]: !!mainContentClass,
          fit: !$slots.banner,
        }"
        :style="{
          padding: noPadding ? 0 : undefined,
        }"
      >
        <re-title
          v-if="pageTitle || tooltip"
          :tooltip="tooltip"
          :class="centerTitle ? 'row justify-center' : ''"
        >
          {{ pageTitle }}
        </re-title>
        <slot />
      </div>
    </div>
    <q-page-sticky
      v-if="showBackground"
      :class="{ 'themed-asset': themedBackground }"
      position="bottom-left"
      :style="{ ...defaultBackgroundStyle, ...backgroundStyle }"
    />
  </q-page>
</template>
<script setup lang="ts">
import { useQuasar } from 'quasar'
import { computed, ref } from 'vue'

import { useFlow } from '@/composables/useFlow'

import ReStepper from './ReStepper.vue'

const {
  maxWidth = 'md',
  tooltip = undefined,
  backgroundStyle = undefined,
  themedBackground = false,
  showBackground = false,
  pageTitle = '',
  centerTitle = false,
  isCentered = false,
  mainContentClass = 're-main-content',
  flow = undefined,
  flowProps = undefined,
  stacked = undefined,
  stickyTop = true,
  markStepsDone = true,
} = defineProps<{
  maxWidth?: string
  tooltip?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  backgroundStyle?: Record<string, any>
  themedBackground?: boolean
  showBackground?: boolean
  pageTitle?: string
  centerTitle?: boolean
  isCentered?: boolean
  mainContentClass?: string
  flow?: ReturnType<typeof useFlow>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  flowProps?: Record<string, any>
  stacked?: boolean
  stickyTop?: boolean
  markStepsDone?: boolean
  noPadding?: boolean
}>()

const q = useQuasar()

const isStacked = computed(() => stacked ?? q.screen.lt.md)

const backgroundUrl = new URL('@/assets/icons/img-skyline.svg', import.meta.url).pathname

const defaultBackgroundStyle = ref({
  background: `url('${backgroundUrl}') no-repeat`,
  backgroundPositionY: 'bottom',
  backgroundPositionX: 'center',
  height: '100%',
  width: '100%',
  zIndex: -100,
})
</script>
<style scoped lang="scss">
.page {
  .sidebar {
    background-color: $base;
    z-index: 1001;
  }

  .limit-width-above-sm {
    @media (min-width: $breakpoint-sm-max) {
      max-width: 300px;
    }
  }

  &.re-stacked {
    display: grid;
    grid-template-columns: 1fr;

    &.with-sidebar {
      grid-template-rows: min-content auto;
    }

    .sticky-top {
      top: $navbar-height;
    }

    &.re-centered {
      justify-content: center;
      justify-items: center;
    }
  }

  &.re-side-by-side {
    display: flex;
    flex-flow: row nowrap;

    .page__main {
      width: 100%;
    }

    &.with-sidebar {
      --sidebar-min-width: 250px; // FIXME: define reactive min stepper width

      .page__main {
        margin: 0;
        width: calc(100% - var(--sidebar-min-width, 0px));
      }

      .sidebar {
        flex-shrink: 0;
        min-width: var(--sidebar-min-width);
        height: calc(100vh - $navbar-height); // Needed for sticky to work
      }
    }

    &.re-centered {
      justify-content: center;
      justify-items: center;
    }
  }

  .sticky-top {
    position: sticky;
    top: $navbar-height;
    overflow-y: auto;
  }
}
</style>
